// ModelCard — the grid-view card on the /models catalog page.
//
// Layout: tinted hero band (color from primary output kind) with the
// company logo centered, then a body with `provider/model` title, a
// 3-line description, and up to 3 colored tag chips (color sourced
// from `colorForSlug` so the same tag stays the same color across the
// entire catalog UI).
//
// PropTypes-style contract:
//   model       — full model record from the model store
//   company     — full company record (or null/undefined)
//   onPress(id) — invoked with the model id on tap
//
// Loading state is built in: pass `isLoading` and the card renders a
// shimmer-friendly skeleton instead, so the catalog grid keeps its
// rhythm while data hydrates.

import React, { useMemo, useState } from 'react';
import { Platform, Pressable, StyleSheet, View } from 'react-native';
import { Image } from 'expo-image';
import PropTypes from 'prop-types';
import { useTheme } from '@/context/ThemeContext';
import { PLUS_COLORS } from '@/constants/Colors';
import Text from '@/components/Typography/Text';
import Icon from '@/components/Icon';
import {
  colorForSlug,
  getPrimaryOutput,
  isNewModel,
} from '@/utils/model_catalog';

const ModelCard = ({ model, company, onPress, isLoading }) => {
  const { colors } = useTheme();
  const [hovered, setHovered] = useState(false);

  const styles = useMemo(() => createStyles(colors), [colors]);

  if (isLoading) {
    return (
      <View style={styles.container}>
        <View style={[styles.hero, { backgroundColor: colors.solid_color_tertiary }]} />
        <View style={{ padding: 12, gap: 8 }}>
          <View style={{ height: 14, width: '70%', borderRadius: 4, backgroundColor: colors.solid_color_tertiary }} />
          <View style={{ height: 12, width: '40%', borderRadius: 4, backgroundColor: colors.solid_color_tertiary }} />
          <View style={{ height: 32, borderRadius: 4, backgroundColor: colors.solid_color_tertiary, marginTop: 4 }} />
        </View>
      </View>
    );
  }

  if (!model) return null;

  const primary  = getPrimaryOutput(model);
  const isNew    = isNewModel(model);
  const isPlus   = !!model?.flags?.isPlusOnly;
  const isOSS    = !!model?.flags?.isOpenSource;
  const baseCost = model?.creditCosts?.baseCost;
  // Filter out tag refs that didn't resolve to a usable label — happens
  // when a model points at a tag without a `name`/`slug` (or a deleted
  // tag the populate left as a bare id). Without this, the chip row
  // ends up with empty grey rectangles.
  const tags = (Array.isArray(model.tags) ? model.tags : [])
    .filter(tg => tg && (tg.name || tg.slug));

  return (
    <Pressable
      onPress={() => onPress?.(model.id)}
      onHoverIn={() => setHovered(true)}
      onHoverOut={() => setHovered(false)}
      style={[
        styles.container,
        { borderColor: hovered ? primary.color : colors.border_color_secondary },
        Platform.OS === 'web' && {
          cursor: 'pointer',
          transition: 'border-color 120ms ease, transform 120ms ease',
          transform: hovered ? 'translateY(-2px)' : 'translateY(0)',
        },
      ]}
    >
      <View style={[styles.hero, { backgroundColor: `${primary.color}14` }]}>
        {model.logoPath
          ? <Image source={{ uri: model.logoPath }} style={styles.heroLogo} contentFit="cover" />
          : <Icon name={primary.icon} size={36} color={primary.color} />
        }

        {/* Top-left: NEW badge */}
        {isNew && (
          <View style={styles.newBadge}>
            <Icon name="sparkles" solid size={8} color="#fff" />
            <Text size="xxs" color="white" weight="bold">New</Text>
          </View>
        )}

        {/* Top-right: stack of status pills (PLUS over OSS). Selection
            doesn't apply here so we reuse the slot for monetization +
            licensing badges that the user actually cares about when
            scanning a card. */}
        <View style={styles.cornerStack}>
          {isPlus && (
            <View style={styles.plusBadge}>
              <Icon name="sparkles" solid size={8} color={PLUS_COLORS.iconColor} />
              <Text
                size="xxs"
                weight="extraBold"
                style={{ color: PLUS_COLORS.textColor, textShadow: PLUS_COLORS.textShadow }}
              >
                PLUS
              </Text>
            </View>
          )}
          {isOSS && (
            <View style={[styles.statusBadge, { backgroundColor: colors.solid_color_block }]}>
              <Text size="xxs" color="secondary" weight="bold">OSS</Text>
            </View>
          )}
        </View>
      </View>

      <View style={styles.body}>
        <View style={styles.titleRow}>
          <Text size="n" weight="bold" color="primary" numberOfLines={1} style={{ flexShrink: 1 }}>{model.name}</Text>
          {baseCost != null && baseCost > 0 && (
            <View style={styles.cost}>
              <Icon name="coin" solid size={9} color={colors.text_color_tertiary} />
              <Text size="xs" color="secondary" weight="semibold" style={{ fontVariant: ['tabular-nums'] }}>{baseCost}</Text>
            </View>
          )}
        </View>

        <Text size="xs" color="secondary" numberOfLines={3} style={styles.description}>
          {model.description || ' '}
        </Text>

        {/* Always render the chip row container so cards keep the same
            total height even when a model has no resolvable tags. */}
        <View style={styles.chipRow}>
          {tags.slice(0, 3).map((tg) => {
            const c = colorForSlug(tg.slug || tg.name || tg.id);
            const label = tg.name || tg.slug;
            return (
              <View
                key={tg.id || tg.slug || tg.name}
                style={[styles.tagChip, { backgroundColor: `${c}29` }]}
              >
                <Text
                  size="xxs"
                  weight="semibold"
                  numberOfLines={1}
                  style={{ color: c, textTransform: 'capitalize' }}
                >
                  {label}
                </Text>
              </View>
            );
          })}
        </View>
      </View>
    </Pressable>
  );
};

ModelCard.propTypes = {
  model: PropTypes.object,
  company: PropTypes.object,
  onPress: PropTypes.func,
  isLoading: PropTypes.bool,
};

const createStyles = (colors) => StyleSheet.create({
  container: {
    backgroundColor: colors.solid_color_block,
    borderRadius: 10,
    borderWidth: 1,
    borderColor: colors.border_color_secondary,
    overflow: 'hidden',
  },
  hero: {
    height: 128,
    alignItems: 'center',
    justifyContent: 'center',
    position: 'relative',
  },
  heroLogo: {
    width: 56, height: 56, borderRadius: 10,
  },
  newBadge: {
    position: 'absolute', top: 8, left: 8,
    flexDirection: 'row', alignItems: 'center', gap: 4,
    backgroundColor: '#22C55E',
    paddingHorizontal: 6, paddingVertical: 2,
    borderRadius: 4,
  },
  newBadgeText: { textTransform: 'lowercase' },
  cornerStack: {
    position: 'absolute', top: 8, right: 8,
    flexDirection: 'row', gap: 4,
  },
  statusBadge: {
    paddingHorizontal: 5, paddingVertical: 2,
    borderRadius: 4,
  },
  // Gradient PLUS pill — matches the model selector and the rest of the
  // app so paid-only signaling stays consistent. Web gets the gradient;
  // native falls back to the solid icon color underneath.
  plusBadge: {
    flexDirection: 'row', alignItems: 'center', gap: 2,
    paddingHorizontal: 5, height: 16, borderRadius: 4,
    backgroundColor: PLUS_COLORS.iconColor,
    ...(Platform.OS === 'web' && { backgroundImage: PLUS_COLORS.backgroundImage }),
  },
  body: {
    padding: 12,
    gap: 8,
  },
  titleRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 8,
    // Single-line title — wrapping here was the main reason cards drifted
    // out of alignment when a provider/slug got long.
    height: 20,
    overflow: 'hidden',
  },
  cost: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 3,
    flexShrink: 0,
  },
  chipRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 4,
    // Reserve the chip row's height even when the model has no tags so
    // every card in the grid lines up to the same total height.
    minHeight: 18,
  },
  tagChip: {
    paddingHorizontal: 6, height: 18, borderRadius: 4,
    justifyContent: 'center',
  },
  description: {
    lineHeight: 16,
    // 3 lines × 16px line-height. Combined with the fixed hero, title
    // row, and chip-row reservations this is what guarantees every
    // card renders at the same height across the grid.
    minHeight: 48,
  },
});

export default React.memo(ModelCard);
